import React, { useContext } from 'react';
import classNames from 'classnames';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import { Button } from '../Button';
import { Icon } from '../Icon';
import styles from './Alert.module.scss';

export const enum AlertTypes {
	SUCCESS = 'SUCCESS',
	WARNING = 'WARNING',
	DANGER = 'DANGER',
}

interface AlertProps {
	children: React.ReactNode;
	className?: string;
	type: AlertTypes;
	onClose?: () => void;
}

/**
 * Alert component, used for showing a message to the user.
 * Appearance can be either success, warning or danger.
 */
export const Alert: React.FunctionComponent<AlertProps> = ({ children, className, type, onClose }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	return (
		<div
			className={classNames(className, styles.wrapper, {
				[styles.success]: type === AlertTypes.SUCCESS,
				[styles.warning]: type === AlertTypes.WARNING,
				[styles.danger]: type === AlertTypes.DANGER,
				[styles.hasCloseButton]: onClose,
			})}
		>
			<Icon className={styles.icon} name={type === AlertTypes.SUCCESS ? 'check' : 'alert'} size={'md'} />

			<div className={styles.content}>{children}</div>

			{onClose && (
				<Button
					variant={'ghost'}
					hasOnlyIcon
					aria-label={translations?.data.shared.close || 'Close'}
					onClick={onClose}
				>
					<Icon name={'close'} size={'md'} />
				</Button>
			)}
		</div>
	);
};
